// Render ```mermaid fences into real diagrams.
//
// Models write mermaid well and unprompted — but with no renderer it arrives as
// a wall of "graph TD; A-->B" text, so asking for a flow chart got you source
// code to paste into some other tool. This is the cheapest good diagram in the
// product: no file, no skill, no image model, no round trip.
//
// mermaid is ~1MB, so it is imported DYNAMICALLY on first use. A chat that never
// shows a diagram never downloads it; Vite splits it into its own chunk.

let mermaidPromise: Promise<typeof import('mermaid').default> | null = null;

/** Load + configure mermaid once, on first diagram. */
function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((m) => {
      const mermaid = m.default;
      mermaid.initialize({
        startOnLoad: false,
        // The diagram text comes from a model, so treat it as untrusted: 'strict'
        // disables click handlers and inline HTML inside nodes.
        securityLevel: 'strict',
        theme: 'base',
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        themeVariables: {
          // Match the chat surface rather than mermaid's default lilac.
          primaryColor: '#f5efe6',
          primaryTextColor: '#1a1a1a',
          primaryBorderColor: '#c9b8a1',
          lineColor: '#8a7a63',
          secondaryColor: '#efe7db',
          tertiaryColor: '#faf7f2',
          fontSize: '14px',
        },
      });
      return mermaid;
    });
  }
  return mermaidPromise;
}

/** True if mermaid can parse `src`. Never throws, and never renders the error
 *  graphic: suppressErrors stops parse() itself from injecting the "Syntax
 *  error" bomb into the page — a lesson from the old web-ui frontend, which
 *  shipped that bug and then fixed it (c2ace17). */
async function safeParse(mermaid: { parse: (s: string, o?: { suppressErrors?: boolean }) => Promise<unknown> }, src: string) {
  try {
    return !!(await mermaid.parse(src, { suppressErrors: true }));
  } catch {
    return false;
  }
}

/** Auto-repair the most common LLM mermaid breakage so a slightly-off diagram
 *  still renders instead of dropping to raw source. Only rewrites the specific
 *  broken patterns below, so a valid diagram passes through untouched.
 *
 *  1. A `[rectangle]` (or `(round)` / `{rhombus}`) label containing punctuation
 *     mermaid parses as syntax — `: = ^ ( ) # ; < > & % /` — when it isn't
 *     already quoted. This is the #1 failure, e.g. `E[Pythagorean: c^2 = a^2+b^2]`.
 *  2. A BARE node written as `ID= text` (or `ID = text`) after an edge, which is
 *     a parse error: `C --> D= a^2 + b^2` becomes `C --> D["a^2 + b^2"]`.
 */
function repairMermaid(src: string): string {
  const quote = (label: string) => `"${label.replace(/"/g, "'").trim()}"`;
  const BREAK = '[:=^()#;<>&%/]';
  // Flow-style diagram? `note …of`, `participant`, and `->>`/`|label|>` are
  // sequenceDiagram tokens the model sometimes leaks into a `graph`/`flowchart`;
  // only rewrite/strip them in a flow context so a real sequence diagram is safe.
  const firstLine = src.replace(/^\s*(?:%%\{[\s\S]*?\}%%\s*)?/, '').trim().split(/\r?\n/)[0] ?? '';
  const isFlow = /^(?:flowchart|graph)\b/i.test(firstLine);
  let out = src
    // 2) bare `--> ID= label` / `--> ID label` → `--> ID["label"]` (run first so
    //    the produced brackets are then covered by the label-quoting pass).
    .replace(
      new RegExp(`((?:--+>|-\\.-+>|==+>|--[xo]|--+))\\s*([A-Za-z]\\w*)\\s*=\\s*([^\\n;\\[]+?)\\s*$`, 'gm'),
      (_m, edge: string, id: string, label: string) => `${edge} ${id}[${quote(label)}]`,
    )
    // 3) stray `>` after an edge label pipe: `A -->|a|> B` → `A -->|a| B`.
    .replace(/(\|[^|\n]*\|)\s*>/g, '$1')
    // 1a) [rectangle] labels
    .replace(new RegExp(`\\[([^\\]\\n"]*${BREAK}[^\\]\\n]*)\\]`, 'g'), (m, l: string) => (/^\s*["'\`]/.test(l) ? m : `[${quote(l)}]`))
    // 1b) {rhombus} labels
    .replace(new RegExp(`\\{([^}\\n"]*[:=^#;<>&%/][^}\\n]*)\\}`, 'g'), (m, l: string) => (/^\s*["'\`]/.test(l) ? m : `{${quote(l)}}`));
  if (isFlow) {
    out = out
      // sequence-only statements mistakenly placed in a flowchart → drop the line.
      // (NOT `end` — that's a valid flowchart `subgraph … end`.)
      .replace(/^[ \t]*(?:note\s+(?:left|right|over)\b|participant\b|activate\b|deactivate\b)[^\n]*$/gim, '')
      // sequence arrows (`->>`, `-->>`) used in a flowchart → normal flow arrow.
      .replace(/-+>>/g, '-->')
      .replace(/\n{3,}/g, '\n\n');
  }
  return out;
}

let seq = 0;

/**
 * Find un-rendered .mermaid-block elements under `root` and draw them.
 *
 * Safe to call on every render/stream tick: each block is marked done, so work
 * happens once. A block whose source does not parse is LEFT as its <pre>
 * fallback rather than replaced with an error — while a reply is still
 * streaming the fence is legitimately half-written, and flashing "syntax error"
 * at the user for one tick is worse than showing the source for one tick.
 */
export async function renderMermaidIn(root: HTMLElement | null): Promise<void> {
  if (!root) return;
  const blocks = Array.from(
    root.querySelectorAll<HTMLElement>('.mermaid-block[data-mermaid]:not([data-mermaid-done])'),
  );
  if (blocks.length === 0) return;

  let mermaid: Awaited<ReturnType<typeof loadMermaid>>;
  try {
    mermaid = await loadMermaid();
  } catch {
    return; // offline / chunk failed → the <pre> source stays, which is readable
  }

  for (const el of blocks) {
    const src = el.getAttribute('data-mermaid') ?? '';
    if (!src.trim()) continue;
    try {
      // parse() first: render() on bad input can leave orphan nodes in the DOM.
      // If it fails, try the conservative repair and re-validate, so a diagram
      // that is only broken in the usual way still renders.
      let out = src;
      let ok = await safeParse(mermaid, out);
      if (!ok) {
        const fixed = repairMermaid(src);
        if (fixed !== src && (await safeParse(mermaid, fixed))) {
          out = fixed;
          ok = true;
        }
      }
      if (!ok) continue; // still streaming, or genuinely invalid → keep the source
      seq += 1;
      const renderId = `brew-mmd-${seq}`;
      try {
        const { svg } = await mermaid.render(renderId, out);
        el.innerHTML = svg;
        el.setAttribute('data-mermaid-done', '1');
      } finally {
        // mermaid.render() appends a temporary node (id `d<renderId>`) to <body>
        // and, when it fails, LEAVES it behind as a big "Syntax error in text"
        // graphic at the bottom of the page — even though the throw is caught
        // below. Remove any orphan so a failed/streaming diagram never leaks that
        // bomb into the page; on success mermaid already removed it (no-op).
        // Only remove a STRAY node left in <body> — NOT the SVG we just inserted
        // into `el`. mermaid gives the rendered SVG id=renderId, so a blind
        // getElementById(renderId).remove() deletes the diagram itself (→ empty box).
        // Never remove the SVG we just inserted into `el` (mermaid gives it
        // id=renderId). Only sweep STRAY nodes mermaid left in <body> — the temp
        // measurement divs (`dbrew-mmd-*`) and error SVGs from a failed/earlier
        // attempt — so a "Syntax error" bomb never lingers at the page bottom.
        const rendered = document.getElementById(renderId);
        if (rendered && !el.contains(rendered)) rendered.remove();
        document.querySelectorAll('[id^="dbrew-mmd-"], svg[aria-roledescription="error"]').forEach((n) => {
          if (!n.closest('.mermaid-block')) n.remove();
        });
      }
    } catch {
      // Incomplete (still streaming) or genuinely invalid — leave the source
      // visible and try again on the next tick.
    }
  }
}